import React, { useEffect, useState } from 'react'
import PageHeader from '../../Components/PageHeader/PageHeader'
import AppTable from '../../Components/AppTable/AppTable'
import { useDispatch, useSelector } from 'react-redux';
import { ApiHit } from '../../Api/ApiHit';
import { deleteExpense, searchExpense } from '../../Constant/Constant';
import { setApiJson } from '../../Redux/Actions/ApiAction';
import { setPagination } from '../../Redux/Actions/TableAction';
import ActionButton from '../../Components/AppButton/ActionButton';
import DeleteActionButton from '../../Components/AppButton/DeleteActionButton';
import { PencilIcon, FileText, ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { hasPermission } from '../../utils';
import { getCahbookUser } from '../../Storage/Storage';

function ExpenseMaster() {
    const TableReducer = useSelector((state) => state.TableReducer);
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    // Get user permissions
    const canEditExpenses = hasPermission('expense', 'write');
    const canReadExpenses = hasPermission('expense', 'read');
    const canDeleteExpenses = hasPermission('expense', 'delete');
    const canAddExpenses = hasPermission('expense', 'write');

    useEffect(() => {
        if (canReadExpenses) {
            getExpense();
        }
    }, [TableReducer.pagination.page, TableReducer.pagination.limit, canReadExpenses]);

    const getExpense = () => {
        if (!canReadExpenses) {
            toast.error('You do not have permission to view expenses');
            return;
        }

        setIsLoading(true);

        const currentUser = getCahbookUser();

        console.log("currentUser",currentUser);
        
        const isNotSuperAdmin = currentUser?.roleId?.roleName !== "SuperAdmin";

        let json = {
            page:  TableReducer.pagination.page,
            limit:  TableReducer.pagination.limit,
            search: isNotSuperAdmin ? { userId: currentUser?._id } : {}
        };

        ApiHit(json, searchExpense).then((res) => {
            console.log("res_+_+", res);

            if (res?.statusCode === 200) {
                dispatch(setApiJson(res?.data?.docs));
                const paginationData = {
                    total: res?.data?.totalDocs || 0,
                    page: isNotSuperAdmin ? 1 : TableReducer.pagination.page,
                    limit: isNotSuperAdmin ? 10 : TableReducer.pagination.limit,
                };
                dispatch(setPagination(paginationData));
            }
        }).catch((error) => {
            console.error("Error fetching expenses:", error);
            toast.error("Error fetching expenses. Please try again.");
        }).finally(() => {
            setIsLoading(false);
        });
    };

    const baseIndex = (TableReducer.pagination.page - 1) * TableReducer.pagination.limit;

    const handleEdit = (row) => {
        if (!canEditExpenses) {
            toast.error('You do not have permission to edit expenses');
            return;
        }
        navigate(`/expense/edit/${row._id}`);
    };

    const handleDelete = (expense) => {
        if (!canDeleteExpenses) {
            toast.error('You do not have permission to delete expenses');
            return;
        }

        ApiHit({ _id: expense?._id }, deleteExpense).then((res) => {
            if (res?.statusCode === 200) {
                toast.success(res?.message || 'Expense deleted successfully');
                getExpense();
            } else {
                toast.error(res?.error || 'Failed to delete expense');
            }
        }).catch((error) => {
            console.error("Error deleting expense:", error);
            toast.error('Failed to delete expense');
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return `₹${amount?.toLocaleString('en-IN') || 0}`;
    };

    const tableHeaders = [
        {
            title: "S.No",
            key: "serialNumber",
            width: "60px",
            align: "center",
            priority: 1,
            render: (_, row, index) => {
                const serialNumber = baseIndex + index + 1;
                return (
                    <div className="text-center">
                        <span className="text-sm font-medium text-gray-900">{serialNumber}</span>
                    </div>
                );
            }
        },
        {
            title: "Title",
            key: "title",
            priority: 1,
            render: (value) => (
                <div className="text-sm text-gray-900">
                    {value || 'N/A'}
                </div>
            )
        },
        {
            title: "User",
            key: "userId",
            priority: 2,
            render: (value) => (
                <div className="text-sm text-gray-900">
                    <div className="font-medium">{value?.name || 'N/A'}</div>
                    <div className="text-xs text-gray-500">{value?.email || ''}</div>
                </div>
            )
        },
        {
            title: "Amount & Date",
            key: "amount",
            priority: 1,
            render: (value, row) => (
                <div className="text-sm text-gray-900">
                    <div className="font-medium">{formatCurrency(value)}</div>
                    <div className="text-xs text-gray-500">{formatDate(row?.date)}</div>
                </div>
            )
        },
        {
            title: "Category & Payment",
            key: "category",
            priority: 2,
            render: (value, row) => (
                <div className="text-sm text-gray-900">
                    <div className="font-medium">{value || 'N/A'}</div>
                    <div className="text-xs text-gray-500">{row?.paymentMethod || 'N/A'}</div>
                </div>
            )
        },
        {
            title: "Reimbursement",
            key: "reimbursement",
            priority: 2,
            align: "center",
            render: (value) => (
                <div className="text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value === 'Yes' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                        {value || 'N/A'}
                    </span>
                </div>
            )
        },
        {
            title: "Proof",
            key: "proofUrl",
            priority: 3,
            align: "center",
            render: (value) => {
                if (!value) {
                    return (
                        <div className="text-center text-xs text-gray-400">
                            No proof
                        </div>
                    );
                }

                // Check if it's an image or PDF
                const isImage = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(value);
                const isPDF = /\.pdf$/i.test(value);

                if (isImage) {
                    return (
                        <div className="flex justify-center">
                            <a
                                href={value}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="relative group"
                            >
                                <img
                                    src={value}
                                    alt="Expense proof"
                                    className="w-12 h-12 object-cover rounded border border-gray-200 hover:border-blue-500 transition-colors"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentElement.innerHTML = '<div class="w-12 h-12 flex items-center justify-center bg-gray-100 rounded border border-gray-200"><span class="text-gray-400 text-xs">Error</span></div>';
                                    }}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity rounded flex items-center justify-center">
                                    <ImageIcon className="w-4 h-4 text-white opacity-0 group-hover:opacity-100" />
                                </div>
                            </a>
                        </div>
                    );
                } else if (isPDF) {
                    return (
                        <div className="flex justify-center">
                            <a
                                href={value}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center w-12 h-12 bg-red-50 rounded border border-red-200 hover:border-red-500 transition-colors group"
                            >
                                <FileText className="w-5 h-5 text-red-500 group-hover:text-red-600" />
                            </a>
                        </div>
                    );
                } else {
                    return (
                        <div className="flex justify-center">
                            <a
                                href={value}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-500 hover:text-blue-600 underline"
                            >
                                View
                            </a>
                        </div>
                    );
                }
            }
        },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (_, row) => {
                // Check if user has any action permissions
                const hasAnyPermission = canEditExpenses || canDeleteExpenses;

                if (!hasAnyPermission) {
                    return (
                        <div className="flex justify-center">
                            <span className="text-sm text-gray-400">No actions available</span>
                        </div>
                    );
                }

                return (
                    <div className="flex justify-center space-x-2">
                        {canEditExpenses && (
                            <ActionButton
                                onClick={() => handleEdit(row)}
                                tooltip="Edit Expense"
                                icon={<PencilIcon className='w-4 h-4' />}
                            />
                        )}
                        {canDeleteExpenses && (
                            <DeleteActionButton
                                onConfirm={() => handleDelete(row)}
                                itemName="expense"
                                tooltip="Delete Expense"
                            />
                        )}
                    </div>
                );
            }
        }
    ];

    // Filter out Actions column if user has no action permissions
    const hasAnyActionPermission = canEditExpenses || canDeleteExpenses;
    const filteredHeaders = hasAnyActionPermission
        ? tableHeaders
        : tableHeaders.filter(header => header.key !== 'actions');

    // Show access denied message if user doesn't have read permission
    if (!canReadExpenses) {
        return (
            <div>
                <PageHeader
                    title={'Expense Management'}
                    description={'Track, view, and manage all business expenses efficiently'}
                />
                <div className="flex justify-center items-center h-64">
                    <div className="text-center">
                        <h3 className="text-lg font-medium text-red-600 mb-2">Access Denied</h3>
                        <p className="text-gray-600">You don't have permission to view expenses.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <PageHeader
                title={'Expense Management'}
                description={'Track, view, and manage all business expenses efficiently'}
            />
            <div className="mt-5">
                <AppTable
                    TH={filteredHeaders}
                    TD={ApiReducer.apiJson || []}
                    emptyMessage="No expense found"
                    hidePagination={false}
                    buttonText={canAddExpenses ? "Add Expense" : undefined}
                    navigateTo={canAddExpenses ? "/expense/add" : undefined}
                />
            </div>
        </div>
    )
}

export default ExpenseMaster
